import React, { useEffect, useState } from 'react';
import Icon from '../Icon';
import PropTypes from 'prop-types';
import cx from '@/utils/cx';
import useStyle from './style';

function Dropdown({
  items,
  multiSelect,
  setValue,
  title,
  transitionTime,
  value,
  zIndex,
  disabled,
  maxHeight,
  onChange,
  tabIndex,
  className,
  ...rest
}) {
  const SINGLE_ITEM = 1;
  const [isOpen, setIsOpen] = useState(false);
  const classes = useStyle({ isOpen, zIndex, transitionTime, maxHeight });

  const closeDropdown = () => setIsOpen(false);

  useEffect(closeDropdown, [disabled]);

  const toggleDropdown = () => {
    if (disabled) {
      return;
    }
    setIsOpen(!isOpen);
  };

  const handleItemClick = item => {
    if (alreadySelectedSignelSelect() || isDuplicate(item)) {
      return;
    }

    setValue([...value, item]);

    if (onChange) {
      onChange(item);
    }

    if (!multiSelect) {
      closeDropdown();
    }
  };

  const alreadySelectedSignelSelect = () =>
    !multiSelect && value.length === SINGLE_ITEM;

  const isDuplicate = ({ key }) => value.find(item => item.key === key);

  const handleRemoveItemClick = (e, item) => {
    e.stopPropagation();
    setValue(removeItem(item));
  };

  const removeItem = ({ key }) => value.filter(e => e.key !== key);

  const renderSelectedItem = item => (
    <div
      className={cx(classes.selectedItem)}
      _margin='sm'
      _padding='sm'
      key={item.key}
    >
      <p className={cx(classes.selectedItemTitle)} _text_size='md'>
        {item.value}
      </p>
      <Icon
        className={cx(classes.closeIcon)}
        size={16}
        _margin='sm'
        onClick={e => handleRemoveItemClick(e, item)}
        src={'https://passenger-pwa-cdn.snapp.ir/icons/close.svg'}
      />
    </div>
  );

  const renderItems = item => (
    <div
      _padding='md'
      className={cx(classes.listItem)}
      key={item.key}
      onClick={() => handleItemClick(item)}
    >
      {item.value}
    </div>
  );

  return (
    <div
      tabIndex={tabIndex}
      onBlur={closeDropdown}
      _border='sm'
      className={cx(classes.dropdown, disabled && classes.disabled, className)}
      {...rest}
    >
      <div
        _padding='md'
        className={cx(classes.header)}
        onClick={toggleDropdown}
      >
        <p _padding='sm' _text_size='lg'>
          {title}
        </p>
        <div className={cx(classes.selectedItemsContainer)}>
          {value.map(renderSelectedItem)}
        </div>
        <Icon
          rotation={isOpen ? -90 : 90}
          size={24}
          src={'https://passenger-pwa-cdn.snapp.ir/icons/chevron-right.svg'}
        />
      </div>

      <div _shadow='md' className={cx(classes.list)}>
        {items.map(renderItems)}
      </div>
    </div>
  );
}

Dropdown.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  multiSelect: PropTypes.bool,
  setValue: PropTypes.func,
  title: PropTypes.string.isRequired,
  transitionTime: PropTypes.number,
  value: PropTypes.arrayOf(PropTypes.object),
  zIndex: PropTypes.number,
  disabled: PropTypes.bool,
  maxHeight: PropTypes.number,
  onChange: PropTypes.func,
  tabIndex: PropTypes.number,
  className: PropTypes.string,
};

Dropdown.defaultProps = {
  multiSelect: false,
  setValue: () => {},
  transitionTime: 300,
  value: '',
  zIndex: 2,
  disabled: false,
  maxHeight: 200,
  onChange: () => {},
  tabIndex: 0,
  className: '',
};

export default Dropdown;
